import { useWatchfaceDevices, WatchfaceDeviceType } from '@/hooks';
import { ElectronApi } from '@/services/electron.api';
import { Attributes } from '@/services/shape/attributes';
import { DataSourceFormat } from './enum';
const getWhoelDevices = () => {
  const { wholeDevices } = useWatchfaceDevices();
  return wholeDevices.value.map((item) => item.deviceType);
};

export const deviceFeatureRules: {
  name: CanUseAttr;
  // 只允许在此范围内的设备
  includeTypes?: WatchfaceDeviceType[];
  // 只允许不在此范围内的设备
  excludeTypes?: WatchfaceDeviceType[];
  errMsg: string;
}[] = [
  // 文本18号字体：支持文本元素的设备均可用，内部版含Q系列/P系列
  {
    name: 'text-font-size-18',
    includeTypes: ElectronApi.isProfessional()
      ? [
          'Q66',
          'Q61',
          'Q63',
          'P67',
          'P65',
          'P62',
          'O61',
          'O63',
          'O66',
          'O62M',
          'O65M',
          'O65',
          'O62',
          'N67',
          'N66',
          'N62S'
        ]
      : [
          'P67',
          'P65',
          'O61',
          'O63',
          'O66',
          'O62M',
          'O65M',
          'O65',
          'O62',
          'N67',
          'N66',
          'N62S',
          'P62'
        ],
    errMsg: '该设备不支持18号字体'
  },
  {
    name: 'verticalAlign',
    includeTypes: [
      'N66',
      'N62S',
      'O62',
      'O62M',
      'O61',
      'O63',
      'Q63',
      'P65',
      'P62',
      'P67'
    ],
    errMsg: '不支持弧形文本垂直对齐属性'
  },
  // xy为负
  {
    name: 'layout-negative',
    includeTypes: ElectronApi.isProfessional()
      ? [
          'N67',
          'O62',
          'O66',
          'O63',
          'Q61',
          'Q63',
          'O61',
          'P65',
          'P62',
          'O62M',
          'P67',
          'Q66'
        ]
      : ElectronApi.isStaging()
      ? ['O66', 'O63', 'O61', 'Q61', 'P67']
      : ['O63', 'O61', 'P67'],
    errMsg: '图层坐标不可小于0'
  },
  {
    name: 'widget-gap-negative',
    // excludeTypes: ['N60', 'M66', 'M62', ],
    excludeTypes: ['N60', 'N65', 'M62', 'M66', 'M67', 'L61', 'L67'],
    errMsg: '自动对齐间距不可为负'
  },
  //表盘功耗
  {
    name: 'power-class',
    includeTypes: [
      'N62S',
      'O62',
      'O62M',
      'O65M',
      'O61',
      'O63',
      'Q61',
      'Q63',
      'P65',
      'P62'
    ],
    errMsg: '不支持该功能'
  },
  {
    name: 'letter-space-negative',
    excludeTypes: [
      'L61Simulator',
      'L61',
      'L67',
      'N60',
      'N65',
      'M67',
      'M62',
      'M65S',
      'M66',
      'N65A',
      'N65B',
      'N65C'
      // 以上设备之后的设备都支持
    ],
    errMsg: '文字间距不能为负'
  },
  {
    name: 'multi-AOD-themes',
    includeTypes: [
      'N62',
      'N66',
      'N62S',
      'O62',
      'O62M',
      'O63',
      'Q61',
      'Q63',
      'Q66',
      'N67',
      'O65',
      'O66',
      'O65M',
      'O61',
      'P65',
      'P62',
      'P67'
    ],
    errMsg: '不支持多样式息屏'
  },
  {
    name: 'multi-themes',
    excludeTypes: ['M65S', 'M66'],
    errMsg: '不支持多样式'
  },
  {
    name: 'single-AOD-themes',
    excludeTypes: ['N65A'],
    errMsg: '不支持单一息屏及，不需要存在息屏'
  },
  {
    name: 'dualtime',
    includeTypes: [
      'N62',
      'N66',
      'N62S',
      'O62',
      'O62M',
      'O63',
      'Q61',
      'Q63',
      'Q66',
      'N67',
      'O65',
      'O65M',
      'O66',
      'O61',
      'P65',
      'P62',
      'P67'
    ],
    errMsg: '不支持使用双时区功能'
  },
  {
    name: 'multi-color-group',
    includeTypes: [
      'N62',
      'N66',
      'N62S',
      'N67',
      'O65',
      'O65M',
      'N65A',
      'N65B',
      'N65C',
      'O62',
      'O62M',
      'O63',
      'Q61',
      'Q63',
      'Q66',
      'O66',
      'O61',
      'P65',
      'P62',
      'P67'
    ],
    errMsg: '不支持多配色'
  },
  {
    name: 'shape-text',
    excludeTypes: [
      'N65A',
      'N65B',
      'N65C',
      'N65',
      'M67',
      'N60',
      'M62',
      'M65S',
      'M66',
      'N62'
      // 外部版本时需要将N62排除, 基于jira: https://jira.n.xiaomi.com/browse/BPGJ-399移除
      // ...((ElectronApi.isNormal() ? ['N62'] : []) as WatchfaceDeviceType[])
    ],
    errMsg: '不可使用文本元素'
  },
  // 是否支持多语言
  {
    name: 'multi-locals',
    excludeTypes: [
      'L61',
      'L61Simulator',
      'N60',
      'M65S',
      'M66',
      'N65C',
      'N65A',
      'N65B'
    ],
    errMsg: '不支持多语言'
  },
  {
    name: 'portraitBg',
    includeTypes: ElectronApi.isNormal()
      ? []
      : [
          'N62',
          'N66',
          'N62S',
          'O62',
          'O62M',
          'O63',
          'Q61',
          'Q63',
          'Q66',
          'N67',
          'O65',
          'O65M',
          'O66',
          'O61',
          'P65',
          'P62',
          'P67'
        ],
    errMsg: '不可使用人像表盘'
  },
  {
    name: 'photoAlbumBg',
    includeTypes: [
      'M65S',
      'M67',
      'N62',
      'N66',
      'N62S',
      'N67',
      'O65',
      'O65M',
      'N65A',
      'N65B',
      'N65C',
      'O62',
      'O62M',
      'O63',
      'Q61',
      'Q63',
      'Q66',
      'O66',
      'O61',
      'P65',
      'P62',
      'P67'
    ],
    errMsg: '不可使用相册表盘'
  },
  {
    name: 'slot-font',
    includeTypes: [
      'N62',
      'N66',
      'N62S',
      'O62',
      'O62M',
      'O63',
      'Q61',
      'Q63',
      'Q66',
      'N67',
      'N65A',
      'N65B',
      'O65',
      'O65M',
      'O66',
      'O61',
      'P65',
      'P62',
      'P67'
    ],
    errMsg: '不可用更换字体样式'
  },
  // 是否允许图组的unitIcon属性
  {
    name: 'tempature-with-unit',
    excludeTypes: [
      'L61Simulator',
      'L61',
      'L67',
      'N60',
      'N65',
      'M67',
      'M62',
      'M65S',
      'M66',
      'N62',
      'N62S',
      'N66',
      'N65A',
      'N65B'
      // 这些设备之后的设备都需要支持
    ],
    errMsg: '温度数据源不可使用单位'
  },

  // 是否可用温度华氏度
  {
    name: 'temperature-fahrenheit',
    excludeTypes: ['M66'],
    errMsg: '设备不可用温度华氏度'
  },

  // 图片&图组->数字填充元素下，属性->绑定数据选择了“指南针角度”，增加“指南针旋转” 指南针旋转（帧率可配置 默认50帧）
  {
    name: 'compass-rotation',
    includeTypes: ['O61', 'Q61'],
    errMsg: '该设备不支持指南针旋转'
  },

  // 是否可用默认id
  {
    name: 'auto-watchface-id',
    excludeTypes: ['L61Simulator', 'L61', 'L67', 'M62', 'M65S'],
    errMsg: '设备不可用默认id'
  },

  // 是否可用Animation动效
  {
    name: 'animation',
    includeTypes:
      (ElectronApi.isStaging() && ElectronApi.isProfessional()) ||
      import.meta.env.DEV
        ? [
            'O61',
            'O62',
            'O62M',
            'O63',
            'Q61',
            'Q63',
            'Q66',
            'P65',
            'O66',
            'P62',
            'P67'
          ]
        : ElectronApi.isProfessional()
        ? ['O61', 'O63', 'Q61', 'Q63', 'Q66', 'P65', 'O66', 'P62', 'P67']
        : ['O61', 'O63', 'P65', 'O66', 'P62', 'P67'],
    errMsg: '该设备不支持IFTTT动效功能'
  },

  // IFTTT亮屏动效预览图
  // 内部版本: Q66, Q63, Q61, P67, P65, P62, O63, O61, O66
  // 外部版本: P67, P65, O63, O61, O66
  {
    name: 'animation-preview',
    includeTypes:
      ElectronApi.isProfessional() || ElectronApi.isStaging()
        ? ['Q66', 'Q63', 'Q61', 'P67', 'P65', 'P62', 'O63', 'O61', 'O66']
        : ElectronApi.isNormal()
        ? ['P67', 'P65', 'O63', 'O61', 'O66']
        : [],
    errMsg: '该设备不支持IFTTT动效预览图'
  },

  // IFTTT AOD动效预览图
  // 仅限内部版本: Q66, Q63, Q61
  {
    name: 'animation-preview-aod',
    includeTypes:
      ElectronApi.isProfessional() || ElectronApi.isStaging()
        ? ['Q66', 'Q63', 'Q61']
        : [],
    errMsg: '该设备不支持IFTTT AOD动效预览图'
  },

  // AOD下是否可用IFTTT动效编辑
  // 仅限内部版本: Q66, Q63, Q61
  {
    name: 'animation-in-AOD',
    includeTypes:
      ElectronApi.isProfessional() || ElectronApi.isStaging()
        ? ['Q66', 'Q63', 'Q61']
        : [],
    errMsg: '该设备息屏不支持IFTTT动效'
  },

  // 是否可以视频表盘 mp4 视频表盘
  {
    name: 'shape-video',
    includeTypes: ElectronApi.isProfessional() ? ['O62M', 'O61', 'Q61'] : [],
    errMsg: '设备不可用视频表盘'
  },
  // 电量划分为0-11，11-21，...，91-100
  {
    name: 'battery-special-cut',
    includeTypes: ['N67', 'O65', 'O65M'],
    errMsg: '电量特殊划分'
  },
  // 是否支持索引色
  {
    name: 'indexed-color',
    excludeTypes: ['M62', 'M65S', 'M66'],
    errMsg: '设备不支持索引色'
  },
  {
    name: 'shape-js-lua',
    excludeTypes: ['M62', 'M66', 'O61', 'Q61'],
    errMsg: '设备不支持JS/LUA'
  },
  //视频表盘
  {
    name: 'shape-apk',
    includeTypes:
      ElectronApi.isStaging() || import.meta.env.DEV
        ? ['P62', 'P65', 'P67', 'Q61']
        : ElectronApi.isProfessional()
        ? ['P62', 'P65', 'P67', 'Q61']
        : [],
    errMsg: '设备不支持apk/rpk文件'
  },
  {
    name: 'data-source-bind-max-min-value-support',
    excludeTypes: ['M65S', 'M66'],
    errMsg: '数据源的最大、最小数值，无法再绑定，只能是数值'
  },

  // 是否可用自动布局
  {
    name: 'widget-flex',
    excludeTypes: ['M65S', 'M66', 'M62'],
    errMsg: '不支持自动布局'
  },
  {
    name: 'compress',
    excludeTypes: ['M62'],
    errMsg: '不支持压缩方式'
  },
  {
    name: 'pointer-light-source',
    excludeTypes: ['M62', 'M66', 'M67'],
    errMsg: '不支持指针光源'
  },
  {
    name: 'jump-application',
    excludeTypes: ['M62', 'M66'],
    errMsg: '不支持跳转应用'
  },
  {
    name: 'slot-border-image',
    excludeTypes: ['M62'],
    errMsg: '不支持小部件边框图'
  },
  // 旋转角度不可为负
  {
    name: 'rotation-cannotbe-negative',
    excludeTypes: ['M65S', 'M66'],
    errMsg: '旋转度数不可为负'
  },
  {
    name: 'associated-data-item',
    excludeTypes: ['M65S', 'M66'],
    errMsg: '属性的关联数据项没有内置翻译'
  },
  // 是否使用新版天气对照表
  // https://xiaomi.f.mioffice.cn/sheets/shtk4MLZOdfRY7B3TM3StLBGjvd
  {
    name: 'new-weather-comparison',
    excludeTypes: [
      'L61Simulator',
      'L61',
      'L67',
      'N60',
      'N65',
      'M67',
      'M62',
      'M65S',
      'M66',
      'N62',
      'N65A',
      'N65B',
      'N65C'
      // 'N62S',
      // 'N66',
      // 'N67',
      // 'O62', 'O62M',
      // 'O65', 'O65M'
    ],
    errMsg: ''
  },
  {
    name: 'push-then-preview',
    excludeTypes: ['M62', 'M65S', 'M66', 'N65A', 'N65B', 'N65C'],
    errMsg: ''
  },
  {
    name: 'image-array-single-ram',
    excludeTypes: [
      'L61Simulator',
      'L61',
      'L67',
      'N60',
      'N65',
      'M67',
      'M62',
      'M65S',
      'M66',
      'N62',
      'N65A',
      'N65B',
      'N65C'
      // 以下设备开始支持图组内存通过单个图片计算
      // 'N62S'
      // 'N66',
      // 'N67',
      // 'O62', 'O62M',
      // 'O65', 'O65M'
    ],
    errMsg: ''
  },

  {
    name: 'shape-progress-bar',
    excludeTypes: ['M66'],
    errMsg: '进度条不可用'
  },

  //不支持单序列帧
  {
    name: 'shape-sprite',
    excludeTypes: ['M66', 'N65A', 'N65B', 'N65C'],
    errMsg: '不支持序列帧'
  },

  {
    name: 'shape-image-array-empty',
    excludeTypes: ['M66'],
    errMsg: '不支持数组空状态'
  },

  // 是否可用图组旋转属性
  {
    name: 'image-array-rotation',
    excludeTypes: ['M66'],
    errMsg: '该设备不支持图组旋转'
  },

  // 是否可用文字表盘
  {
    name: 'text-watchface',
    includeTypes: ElectronApi.isProfessional()
      ? [
          'O66',
          'N67',
          'O62',
          'O62M',
          'O61',
          'O63',
          'Q61',
          'Q63',
          'Q66',
          'P65',
          'P62',
          'P67'
        ]
      : [],
    errMsg: ''
  },
  // 是否可用普通的自定义视频表盘
  {
    name: 'video-watchface',
    includeTypes:
      // 仅限staging环境中开放
      ElectronApi.isProfessional() || import.meta.env.DEV
        ? ['O62M', 'O61', 'Q61']
        : [],
    errMsg: '该设备不支持自定义视频表盘'
  },

  // 是否设置数据源默认值
  {
    name: 'set-default-data-source',
    excludeTypes: [
      'L61Simulator',
      'L61',
      'L67',
      'N60',
      'N65',
      'M67',
      'M62',
      'M65S',
      'M66',
      'N62',
      'N62S',
      'N65A',
      'N65B',
      'N65C',
      'N66',
      'N67',
      'O62M'
    ],
    errMsg: '该设备不支持设置数据源默认值'
  },
  //Rive动效
  {
    name: 'rive',
    includeTypes:
      ElectronApi.isProfessional() || import.meta.env.DEV
        ? ['O61', 'P62', 'Q61', 'Q63']
        : [],
    errMsg: ''
  },
  // 是否支持容器
  {
    name: 'shape-slot',
    excludeTypes: ['M66'],
    errMsg: '该设备不支持容器'
  },
  // 是否支持复杂功能
  {
    name: 'shape-app-widget',
    excludeTypes: ['M66', 'N65', 'N65A', 'N65B', 'N65C', 'O65', 'O65M'],
    errMsg: '该设备不支持复杂功能'
  },
  // 息屏模式下是否支持复杂功能
  {
    name: 'aod-shape-app-widget',
    includeTypes: ['Q61'],
    errMsg: '息屏模式下不支持复杂功能'
  },
  {
    name: 'multi-sprite-video',
    includeTypes: ['O62M', 'O63', 'Q61', 'Q63', 'O61', 'O66', 'P62', 'P65', 'P67', 'Q66'],
    errMsg: '该设备不支持多视频/多序列帧'
  },

  // 是否支持选择默认语言
  {
    name: 'choose-defualt-lang',
    excludeTypes: [
      'L61Simulator',
      'L61',
      'L67',
      'N60',
      'N65',
      'M67',
      'M62',
      'M65S',
      'M66',
      'N62',
      'N62S',
      'N66',
      'N65A',
      'N65B',
      'N65C',
      'N67',
      'O62M',
      'O65',
      'O65M',
      'O66'
    ],
    errMsg: '该设备不支持选择默认语言'
  },
  // 是否支持选择保加利亚语
  {
    name: 'support-new-language-bulgaria',
    excludeTypes: [
      'L61Simulator',
      'L61',
      'L67',
      'N60',
      'N65',
      'M67',
      'M62',
      'M65S',
      'M66',
      'N62',
      'N62S',
      'N66',
      'N65A',
      'N65B',
      'N65C',
      'N67',
      'O62',
      'O62M',
      'O63',
      'O65',
      'O65M',
      'O66'
    ],
    errMsg: '该设备不支持选择保加利亚语'
  },
  // 是否支持选择立陶宛语
  {
    name: 'support-new-language-lithuanian',
    excludeTypes: [
      'L61Simulator',
      'L61',
      'L67',
      'N60',
      'N65',
      'M67',
      'M62',
      'M65S',
      'M66',
      'N62',
      'N62S',
      'N66',
      'N65A',
      'N65B',
      'N65C',
      'N67',
      'O62',
      'O62M',
      'O63',
      'O65',
      'O65M',
      'O66',
      'P67'
    ],
    errMsg: '该设备不支持选择立陶宛语'
  },

  // 表盘默认时间更改统一为10:09:24
  {
    name: 'default-time-change',
    excludeTypes: ElectronApi.isProfessional()?[
      'L61Simulator',
      'L61',
      'L67',
      'M62',
      'M65S',
    ]:[
      'L61Simulator',
      'L61',
      'L67',
      'N60',
      'N65',
      'M67',
      'M62',
      'M65S',
      'M66',
      'N62',
      'N62S',
      'N66',
      'N65A',
      'N65B',
      'N65C',
      'N67',
      'O62',
      'O62M',
      'O63',
      'O65',
      'O65M',
      'O66'
    ],
    errMsg: '该设备不支持表盘默认时间统一'
  },
  // 容器中 无 组件放在最后
  {
    name: 'none-widget-last',
    excludeTypes: [
      'L61Simulator',
      'L61',
      'L67',
      'M62',
      'M65S',
      'M66',
      'M67',
      'N60',
      'N62',
      'N62S',
      'N65',
      'N66',
      'N65A',
      'N65B',
      'N65C',
      'N67',
      'O62',
      'O62M',
      'O63',
      'O65',
      'O65M',
      'O66'
    ],
    errMsg: '该设备不支持容器中 无 组件放在最后'
  },
  // 设备支持容器跟随设置
  {
    name: 'support-slot-following',
    includeTypes: ElectronApi.isProfessional()
      ? ['O61', 'P62', 'P65', 'Q63', 'P67', 'Q61', 'Q66']
      : ['P62', 'P67', 'O61'],
    errMsg: '该设备不支持容器跟随设置'
  },
  // 组支持点击最小区域展示
  {
    name: 'min-click-zone',
    includeTypes: ElectronApi.isStaging()
      ? ['O61', 'P62', 'P65', 'P67', 'Q61','Q66','Q63']
      : ElectronApi.isProfessional()
      ? ['O61', 'P62', 'P65', 'P67', 'Q61','Q66','Q63']
      : ElectronApi.isNormal()
      ? ['O61', 'P62', 'P65', 'P67', 'Q61','Q66','Q63']
      : [],
    errMsg: '该设备不支持组点击最小区域展示'
  },
  // 文字表盘重着色（配色2-16文字颜色独立编辑）
  {
    name: 'text-watchface-recolor',
    includeTypes: ['Q61', 'Q63'],
    errMsg: '该设备不支持文字表盘重着色'
  },
  // 是否支持跳转应用自定义名称
  {
    name: 'jump-application-custom-name',
    includeTypes:
      ElectronApi.isStaging() && ElectronApi.isProfessional()
        ? ['O61', 'P62', 'P65', 'P67', 'Q61']
        : ElectronApi.isProfessional()
        ? ['O61', 'P62', 'P65', 'P67', 'Q61']
        : [],
    errMsg: '该设备不支持跳转应用自定义名称功能'
  },
  // 支付宝支付内容选择
  {
    name: 'alipay-content',
    includeTypes: ElectronApi.isProfessional()
      ? ['Q61', 'P62', 'Q63', 'O61', 'Q66']
      : ['P62', 'O61'],
    errMsg: '该设备不支持支付宝支付内容选择'
  },
  // 组支持动效
  {
    name: 'group-support-ifttt',
    includeTypes: ['Q63','Q61'],
    errMsg: '该设备组元素不支持ifttt'
  },
  // 是否支持全局添加蒙层
  {
    name: 'global-mask',
    includeTypes: ['Q61','O61'],
    errMsg: '该设备不支持全局蒙层'
  },
  {
    name: 'slot-default-display',
    // Q61、Q63 在研项目需求，仅内部版本可用
    includeTypes: ElectronApi.isProfessional() ? ['Q61', 'Q63'] : [],
    errMsg: '该设备不支持容器列表默认展示项'
  }
];

export const dataSourceFormatDisabled: {
  key: string;
  disabledFormats: DataSourceFormat[];
}[] = [
  {
    key: 'weatherCurrentTempature_temp',
    disabledFormats: [DataSourceFormat.Format_8时]
  }
];

export type CanUseAttr =
  | keyof Attributes
  // 是否支持xy为负
  | 'layout-negative'
  // 是否支持自动对齐间距为负
  | 'widget-gap-negative'
  /**
   * 是否可用功耗分级
   */
  | 'power-class'
  /**
   * 文本letterSpace可以为负
   */
  | 'letter-space-negative'
  /** 是否可以多配色 */
  | 'multi-color-group'
  /** 是否多样式息屏 */
  | 'multi-AOD-themes'
  /** 是否支持多语言 */
  | 'multi-locals'

  /** 是否可用文本 */
  | 'shape-text'

  /** 是否可用双时区 */
  | 'dualtime'

  /** 是否可以使用人像表盘 */
  | 'portraitBg'
  /** 是否可用相册表盘 */
  | 'photoAlbumBg'

  /** 是否可更换字体样式 */
  | 'slot-font'
  | 'slot-dial'
  | 'slot-time'

  /** 温度数据源是否支持带有单位 */
  | 'tempature-with-unit'

  /** 是否可用默认id */
  | 'auto-watchface-id'
  /** 是否支持索引色 */
  | 'indexed-color'

  /** 是否可用单一息屏 */
  | 'single-AOD-themes'

  /** 是否可用IFTTT动画 */
  | 'animation'

  /** 是否可用IFTTT动效预览图 */
  | 'animation-preview'

  /** 是否可用IFTTT AOD动效预览图 */
  | 'animation-preview-aod'

  /** AOD下是否可用IFTTT动效编辑 */
  | 'animation-in-AOD'

  /** 是否可用温度华氏度 */
  | 'temperature-fahrenheit'

  /** 电量划分为0-11，11-21，...，91-100 */
  | 'battery-special-cut'

  /** 是否支持JS/LUA */
  | 'shape-js-lua'

  /** 是否支持apk/rpk */
  | 'shape-apk'

  /** 某数据源的最大、最小数值，无法再绑定，只能是数值 */
  | 'data-source-bind-max-min-value-support'

  /** 不支持自动布局 */
  | 'widget-flex'

  /** 不支持多样式 */
  | 'multi-themes'

  /** 不支持压缩方式 */
  | 'compress'

  /** 不支持指针光源 */
  | 'pointer-light-source'

  /** 不支持跳转应用 */
  | 'jump-application'

  /** 不支持小部件边框图 */
  | 'slot-border-image'

  /** 旋转角度不可为负 */
  | 'rotation-cannotbe-negative'

  /** 属性的关联数据项没有内置翻译 */
  | 'associated-data-item'

  /** 是否可使用新版天气对照表  */
  | 'new-weather-comparison'

  /** 是否可用单图计算图组的内存  */
  | 'image-array-single-ram'

  /** 是否可用推送到手机预览 */
  | 'push-then-preview'

  /** 视频元素 */
  | 'shape-video'

  /** 容器 */
  | 'shape-slot'

  /** 是否可用进度条 */
  | 'shape-progress-bar'

  /** 是否支持单序列帧 */
  | 'shape-sprite'

  /** 是否支持复杂功能 */
  | 'shape-app-widget'

  /** 息屏模式下是否支持复杂功能 **/
  | 'aod-shape-app-widget' 

  /** 是否支持图组空状态 */
  | 'shape-image-array-empty'

  /** 是否可以图组旋转 */
  | 'image-array-rotation'

  /** 指南针旋转 */
  | 'compass-rotation'

  /** 是否支持文字表盘 */
  | 'text-watchface'

  /** 是否可设置数据源默认值 */
  | 'set-default-data-source'

  /** 是否支持rive */
  | 'rive'

  /** 表盘默认时间更改统一为10:09:24 */
  | 'default-time-change'

  /** 是否支持视频表盘 */
  | 'video-watchface'

  /** 是否支持选择默认语言 */
  | 'choose-defualt-lang'

  /** 是否支持多序列帧或多视频 */
  | 'multi-sprite-video'

  /** 容器中 无 组件放在最后 */
  | 'none-widget-last'

  /** 是否支持选择保加利亚 */
  | 'support-new-language-bulgaria'

  /** 是否支持选择立陶宛语 */
  | 'support-new-language-lithuanian'

  /** 支持容器跟随设置 */
  | 'support-slot-following'

  /** 组支持点击最小区域展示 */
  | 'min-click-zone'

  /** 是否支持跳转应用自定义名称 */
  | 'jump-application-custom-name'

  /** 文本18号字体 */
  | 'text-font-size-18'

  /** 支付宝支付内容选择 */
  | 'alipay-content'

  /** 文字表盘重着色 */
  | 'text-watchface-recolor'

  /** 组支持动效 */
  | 'group-support-ifttt'

  /** 全局蒙层 */
  | 'global-mask'

  /** 容器列表默认展示项（按顺序排放，通过 layout parameter 传递默认项） */
  | 'slot-default-display';
